import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE } from '../config';

const Login = ({ onLoginSuccess }) => {
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [focusedInput, setFocusedInput] = useState(null);
  const [greeting, setGreeting] = useState("Welcome Back");
  const navigate = useNavigate();

  // Set Dynamic Greeting based on time
  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting("Good Morning");
    else if (hour < 18) setGreeting("Good Afternoon");
    else setGreeting("Good Evening");
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE}/Auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ loginId, password }),
      });

      if (!response.ok) throw new Error("Invalid credentials");

      const data = await response.json();
      sessionStorage.setItem("token", data.token);
      sessionStorage.setItem("hospitalId", data.hospitalId);
      sessionStorage.setItem("loginId", loginId);
      sessionStorage.setItem("appThemeColor", data.themeColor || "#0088cc");
      sessionStorage.setItem("allowedModules", JSON.stringify(data.modules));

      if (rememberMe) {
        localStorage.setItem("savedLoginId", loginId);
      }

      onLoginSuccess({ isAuthenticated: true });
      navigate("/dashboard");
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  // --- Styles ---

  const containerStyle = {
    display: "flex",
    height: "100vh",
    width: "100%",
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    overflow: "hidden"
  };

  // LEFT SIDE (As finalized)
  const leftSideStyle = {
    flex: "1.5",
    background: "linear-gradient(135deg, rgba(0, 50, 80, 0.92), rgba(0, 20, 40, 0.95)), url('https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&w=2000&q=80')",
    backgroundSize: "cover",
    backgroundPosition: "center",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    padding: "40px 60px",
    color: "white",
    position: "relative"
  };

  // RIGHT SIDE (Enhanced Background)
  const rightSideStyle = {
    flex: "1",
    backgroundColor: "#fcfcfc", // Thoda off-white aankhon ke liye soft
    backgroundImage: "radial-gradient(#e3e3e3 1px, transparent 1px)", // Subtle dot pattern
    backgroundSize: "20px 20px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "40px",
    position: "relative",
    boxShadow: "-10px 0 30px rgba(0,0,0,0.05)"
  };

  // Floating Blob Decoration (Right Side background)
  const blobStyle = {
    position: 'absolute',
    top: '-10%',
    right: '-10%',
    width: '300px',
    height: '300px',
    background: 'linear-gradient(135deg, #e0f7fa 0%, #e1f5fe 100%)',
    borderRadius: '50%',
    filter: 'blur(60px)',
    opacity: 0.6,
    zIndex: 0
  };

  const navbarStyle = {
    display: "flex", justifyContent: "space-between", alignItems: "center", padding: "15px 25px",
    background: "rgba(255, 255, 255, 0.1)", backdropFilter: "blur(12px)", borderRadius: "16px",
    border: "1px solid rgba(255, 255, 255, 0.15)", marginBottom: "40px"
  };

  const navLinkStyle = {
    color: "rgba(255, 255, 255, 0.8)", fontSize: "13px", fontWeight: "500", textDecoration: "none",
    marginRight: "20px", cursor: "pointer", transition: "color 0.2s"
  };

  const featureCardContainer = { display: "flex", gap: "15px", marginTop: "30px" };
  const featureCardStyle = {
    background: "rgba(0, 0, 0, 0.3)", padding: "15px", borderRadius: "12px",
    border: "1px solid rgba(255, 255, 255, 0.1)", flex: 1, backdropFilter: "blur(5px)"
  };

  // Form Styles
  const formWrapperStyle = {
    width: "100%", maxWidth: "380px", zIndex: 1, 
    background: "rgba(255,255,255,0.8)", // Glass effect on form
    padding: "30px", borderRadius: "20px",
    backdropFilter: "blur(10px)",
    boxShadow: "0 8px 32px rgba(0,0,0,0.05)"
  };

  const inputWrapperStyle = (fieldName) => ({
    position: "relative", marginBottom: "20px", transition: "all 0.3s ease",
    transform: focusedInput === fieldName ? "translateY(-2px)" : "none"
  });

  const iconStyle = {
    position: "absolute", left: "15px", top: "50%", transform: "translateY(-50%)",
    color: "#007bff", fontSize: "18px", opacity: 0.7
  };

  const inputStyle = (fieldName) => ({
    width: "100%", height: "50px", paddingLeft: "45px", paddingRight: "15px",
    border: focusedInput === fieldName ? "2px solid #007bff" : "1px solid #e1e4e8",
    borderRadius: "10px", fontSize: "14px", 
    backgroundColor: focusedInput === fieldName ? "#fff" : "#f9f9f9",
    outline: "none", transition: "all 0.3s ease", color: "#333"
  });

  const labelStyle = {
    display: "block", marginBottom: "6px", fontSize: "12px", fontWeight: "700",
    color: "#555", textTransform: "uppercase", letterSpacing: "0.5px"
  };

  const buttonStyle = {
    width: "100%", height: "50px", background: "linear-gradient(90deg, #0062cc 0%, #00c6ff 100%)",
    border: "none", borderRadius: "10px", color: "white", fontSize: "15px", fontWeight: "700",
    letterSpacing: "0.5px", cursor: "pointer", boxShadow: "0 5px 15px rgba(0, 114, 255, 0.3)",
    transition: "transform 0.2s, box-shadow 0.2s", marginTop: "20px"
  };

  // Utility Links Style
  const utilityLinksStyle = {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    fontSize: "13px", marginTop: "10px", color: "#666"
  };

  const footerBadgeStyle = {
    position: "absolute", bottom: "30px", background: "#f1f3f5", padding: "8px 20px",
    borderRadius: "30px", fontSize: "12px", color: "#6c757d", display: "flex",
    alignItems: "center", gap: "8px", border: "1px solid #dee2e6", zIndex: 1
  };

  return (
    <div style={containerStyle}>
      
      {/* --- LEFT SIDE (Same as before) --- */}
      <div className="d-none d-md-flex" style={leftSideStyle}>
        <div>
            <div style={navbarStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '32px', height: '32px', background: '#00c6ff', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold' }}>H</div>
                    <span style={{ fontWeight: "700", letterSpacing: "0.5px", fontSize: "15px" }}>HMS PORTAL</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={navLinkStyle}>Modules</span>
                    <span style={navLinkStyle}>Documentation</span>
                    <span style={navLinkStyle}>Support</span>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.2)', padding: '5px 12px', borderRadius: '20px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <span>📞</span> +91-1800-HMS-HELP
                </div>
            </div>
            <div style={{ marginTop: '40px' }}>
                <div style={{ color: '#00c6ff', fontWeight: 'bold', letterSpacing: '1px', fontSize: '13px', marginBottom: '10px' }}>ENTERPRISE EDITION V3.0</div>
                <h1 style={{ fontSize: "3.5rem", fontWeight: "800", lineHeight: "1.1", marginBottom: "15px" }}>Streamline Your <br/> Hospital Operations.</h1>
                <p style={{ fontSize: "1.1rem", opacity: 0.8, maxWidth: "90%", lineHeight: "1.6" }}>An integrated platform for OPD, IPD, Pharmacy, Lab, and Billing. Manage patient data securely with real-time analytics.</p>
                <div style={featureCardContainer}>
                    <div style={featureCardStyle}><div style={{ fontSize: '20px', marginBottom: '5px' }}>🛡️</div><h4 style={{ fontSize: '14px', margin: '0 0 5px 0' }}>Secure Data</h4><p style={{ fontSize: '11px', margin: 0, opacity: 0.7 }}>256-bit Encryption</p></div>
                    <div style={featureCardStyle}><div style={{ fontSize: '20px', marginBottom: '5px' }}>☁️</div><h4 style={{ fontSize: '14px', margin: '0 0 5px 0' }}>Cloud Sync</h4><p style={{ fontSize: '11px', margin: 0, opacity: 0.7 }}>Real-time Access</p></div>
                    <div style={featureCardStyle}><div style={{ fontSize: '20px', marginBottom: '5px' }}>📊</div><h4 style={{ fontSize: '14px', margin: '0 0 5px 0' }}>Analytics</h4><p style={{ fontSize: '11px', margin: 0, opacity: 0.7 }}>Smart Reports</p></div>
                </div>
            </div>
        </div>
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.2)", paddingTop: "20px", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "12px", opacity: 0.7 }}>
            <div>&copy; 2026 Keycodetech Pvt Ltd. All rights reserved.</div>
            <div style={{ display: 'flex', gap: '15px' }}><span>Privacy Policy</span><span>Terms of Service</span></div>
        </div>
      </div>

      {/* --- RIGHT SIDE (ENHANCED) --- */}
      <div style={rightSideStyle}>
        
        {/* Subtle Background Blob */}
        <div style={blobStyle}></div>

        <div style={formWrapperStyle}>
          
          <div className="mb-4">
            <span style={{ fontSize: "12px", color: "#00c6ff", fontWeight: "bold", textTransform: "uppercase" }}>{greeting}</span>
            <h2 style={{ fontSize: "26px", fontWeight: "800", color: "#2c3e50", marginTop: "5px" }}>Login to Account</h2>
            <p style={{ color: "#7f8c8d", fontSize: "14px" }}>Enter credentials to access admin panel.</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={inputWrapperStyle("loginId")}>
              <label htmlFor="loginId" style={labelStyle}>User ID / Email</label>
              <div style={{ position: "relative" }}>
                <span style={iconStyle}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                </span>
                <input type="text" id="loginId" style={inputStyle("loginId")} placeholder="Enter ID" value={loginId} onChange={(e) => setLoginId(e.target.value)} onFocus={() => setFocusedInput("loginId")} onBlur={() => setFocusedInput(null)} required />
              </div>
            </div>

            <div style={inputWrapperStyle("password")}>
              <label htmlFor="password" style={labelStyle}>Password</label>
              <div style={{ position: "relative" }}>
                <span style={iconStyle}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                </span>
                <input type="password" id="password" style={inputStyle("password")} placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} onFocus={() => setFocusedInput("password")} onBlur={() => setFocusedInput(null)} required />
              </div>
            </div>

            {/* Remember Me & Forgot Password */}
            <div style={utilityLinksStyle}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '5px' }}>
                    <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} style={{ accentColor: '#0088cc' }} />
                    Remember me
                </label>
                <a href="#" style={{ color: '#0088cc', textDecoration: 'none', fontWeight: '600' }} onClick={(e) => e.preventDefault()}>Forgot Password?</a>
            </div>

            {error && <div className="alert alert-danger mt-3" style={{ fontSize: "13px", padding: "10px", borderRadius: "8px" }}>{error}</div>}

            <button type="submit" style={buttonStyle} onMouseOver={(e) => e.target.style.transform = "translateY(-2px)"} onMouseOut={(e) => e.target.style.transform = "translateY(0)"} disabled={isLoading}>
              {isLoading ? "Verifying..." : "Secure Login"}
            </button>

            {/* Alternative Login Options */}
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <p style={{ fontSize: '12px', color: '#aaa' }}>Or login with</p>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '10px' }}>
                    <button type="button" style={{ border: '1px solid #eee', background: 'white', padding: '8px 15px', borderRadius: '50px', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        📱 OTP
                    </button>
                    <button type="button" style={{ border: '1px solid #eee', background: 'white', padding: '8px 15px', borderRadius: '50px', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        🔳 QR Code
                    </button>
                </div>
            </div>

          </form>
        </div>

        <div style={footerBadgeStyle}>
          <span style={{ fontWeight: "400" }}>Technology Partner</span>
          <div style={{ width: "1px", height: "15px", background: "#ccc" }}></div>
          <span style={{ fontWeight: "700", color: "#2c3e50", letterSpacing: "0.5px" }}>KEYCODETECH PVT LTD</span>
        </div>
      </div>
    </div>
  );
};

export default Login;